import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Activity, X, RefreshCw, RotateCcw, Unlock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminNonDivPerfWarmerButton — Round 96b (Feb 2026).
 *
 * Companion to the dedicated quality-scorer button. Renders an admin
 * status badge showing coverage of the non-dividend perf+market_cap
 * warmer (services/non_div_perf_warmer.py) — e.g. "Warmed: 1,247 / 4,393".
 *
 * The warmer hits Massive aggs (1y daily history) + /v3/reference/tickers
 * for market_cap. ~800 tickers/run @ 1.5s each → full universe completes
 * in ~6 nightly cron runs. Cooldown is 18h so manual re-fires next
 * morning aren't blocked. Polls coverage + status while running.
 */
export default function AdminNonDivPerfWarmerButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const pollRef = useRef(null);

  const refresh = async () => {
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/admin/warm-non-div-perf/status`, { withCredentials: true }),
        axios.get(`${API}/admin/warm-non-div-perf/coverage`, { withCredentials: true }),
      ]);
      setStatus(s.data);
      setCoverage(c.data);
      setRunning(!!s.data?.in_progress);
    } catch {
      // silent — endpoint may not be online yet on first load.
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') return;
    refresh();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Active poll while a warm run is in progress. Status endpoint is
    // cheap (single Mongo find) so 3s is fine.
    if (!running) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(refresh, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user?.role !== 'admin') return null;

  const startWarm = async () => {
    setStarting(true);
    setRunning(true);
    try {
      toast.info('Warming non-div perf — runs in background (~20 min for 800 tickers)');
      await axios.post(`${API}/admin/warm-non-div-perf/fire`, null, { withCredentials: true });
      // Don't await — runs in background. Poll picks up progress.
      setTimeout(refresh, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      // 429 cooldown is expected when fired twice in 18h — treat as info.
      if (err?.response?.status === 429) {
        toast.info('Warmer on cooldown', { description: detail });
      } else {
        toast.error('Warmer fire failed', { description: detail });
      }
      setRunning(false);
    } finally {
      setStarting(false);
    }
  };

  // Round 96b (Feb 2026) — Reset cooldown: nukes both `last_ran_at`
  // AND any stuck `in_progress=true` flag. Pair of admin buttons
  // matches every other warmer (CAGR, ETF, Yield-history) so admins
  // have a consistent recovery flow when supervisor reloads strand a
  // run mid-flight (which is exactly what happened on the first fire).
  const resetCooldown = async () => {
    try {
      await axios.post(`${API}/admin/warm-non-div-perf/reset-cooldown`, null, { withCredentials: true });
      toast.success('Cooldown reset — warmer can be fired immediately');
      setRunning(false);
      setTimeout(refresh, 500);
    } catch (err) {
      toast.error('Could not reset cooldown', {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  // Force-clear (nuclear): wipes a stuck in_progress lock WITHOUT
  // touching the cooldown timer. Use when a worker died but you want
  // the next nightly cron to fire on schedule.
  const forceClear = async () => {
    try {
      await axios.post(`${API}/admin/warm-non-div-perf/force-clear`, null, { withCredentials: true });
      toast.success('Stuck lock cleared');
      setRunning(false);
      setTimeout(refresh, 500);
    } catch (err) {
      toast.error('Could not force-clear', {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const lastSummary = status?.last_summary;
  const warmedPct = coverage?.warmed_pct ?? 0;
  const totalNonDiv = coverage?.total_non_div ?? 0;
  const warmedCount = coverage?.warmed ?? 0;
  // Coverage of the two key user-facing fields. The perf coverage is the
  // headline number since that's what fills the 1Mo/6Mo/1Yr columns.
  const perfPct = totalNonDiv ? Math.round((coverage?.with_perf_1y ?? 0) / totalNonDiv * 100) : 0;
  const mcapPct = totalNonDiv ? Math.round((coverage?.with_market_cap ?? 0) / totalNonDiv * 100) : 0;

  // Cooldown countdown — _make_warmer_endpoints exposes
  // `last_completed_at` and the cooldown is 18h.
  const nextRunHoursLeft = (() => {
    if (!status?.last_completed_at || running) return null;
    const last = new Date(status.last_completed_at).getTime();
    const cooldownMs = 18 * 3600 * 1000;
    const remaining = (last + cooldownMs - Date.now()) / 3600000;
    return remaining > 0 ? remaining.toFixed(1) : 0;
  })();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-warm-non-div-perf-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-amber-500/15 hover:bg-amber-500/25 text-amber-900 dark:text-amber-200 border border-amber-500/40 transition-colors"
        title={`Non-Dividend perf + market_cap warmer. Warmed ${warmedCount}/${totalNonDiv} so far.`}
      >
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
        <span>Warm non-div perf</span>
        {totalNonDiv > 0 && (
          <span
            className={`ml-1 px-1.5 py-0 rounded text-[10px] font-bold tracking-tight border ${
              warmedPct >= 90 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
              : warmedPct >= 50 ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
              : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
            }`}
            data-testid="warm-non-div-perf-badge"
          >
            {warmedCount}/{totalNonDiv}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setOpen(false)}
          data-testid="admin-warm-non-div-perf-modal"
        >
          <div
            className="relative w-full max-w-xl bg-card border border-amber-500/40 rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
              <div>
                <h2 className="text-sm font-bold">Non-Div Perf + Market Cap Warmer</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Populates <code className="bg-muted/30 px-1 rounded">perf_1m/6m/1y</code> + <code className="bg-muted/30 px-1 rounded">market_cap</code> for stubs the bulk hydration skipped.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="admin-warm-non-div-perf-close"
                className="p-1 rounded hover:bg-muted/40"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Coverage progress */}
              <div className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-2" data-testid="warm-non-div-coverage">
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <span>Universe Coverage</span>
                  <button
                    onClick={refresh}
                    className="text-muted-foreground hover:text-foreground"
                    title="Refresh"
                    data-testid="warm-non-div-refresh"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </button>
                </div>
                <CoverageRow label="Warmed" current={warmedCount} total={totalNonDiv} pct={warmedPct} tone="amber" />
                <CoverageRow label="With perf_1y" current={coverage?.with_perf_1y ?? 0} total={totalNonDiv} pct={perfPct} tone="sky" />
                <CoverageRow label="With market_cap" current={coverage?.with_market_cap ?? 0} total={totalNonDiv} pct={mcapPct} tone="emerald" />
              </div>

              {/* Last run / Active run */}
              {lastSummary && (
                <div className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-1" data-testid="warm-non-div-last-result">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Last Run</p>
                  <div className="text-xs font-mono space-y-0.5">
                    <div>Candidates: <span className="text-foreground font-bold">{lastSummary.total_candidates ?? '—'}</span></div>
                    <div>Updated: <span className="text-emerald-300 font-bold">{lastSummary.updated ?? '—'}</span></div>
                    <div>No history: <span className={lastSummary.no_history > 0 ? 'text-amber-300' : 'text-muted-foreground'}>{lastSummary.no_history ?? '—'}</span></div>
                    <div>Errors: <span className={lastSummary.error > 0 ? 'text-rose-300' : 'text-muted-foreground'}>{lastSummary.error ?? '—'}</span></div>
                    <div>Duration: <span className="text-foreground">{lastSummary.duration_seconds ? `${Math.round(lastSummary.duration_seconds)}s` : '—'}</span></div>
                    {status?.last_completed_at && (
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Completed: {new Date(status.last_completed_at).toLocaleString()}
                      </div>
                    )}
                    {nextRunHoursLeft != null && nextRunHoursLeft > 0 && (
                      <div className="text-[10px] text-amber-400 mt-1" data-testid="warm-non-div-cooldown">
                        Next manual fire allowed in {nextRunHoursLeft}h (or wait for nightly cron)
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button
                onClick={startWarm}
                disabled={starting || running}
                data-testid="admin-warm-non-div-start"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {starting || running
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Activity className="w-4 h-4" />}
                {running ? 'Warming…' : 'Fire warmer (800 tickers, ~20 min)'}
              </button>

              {/* Round 96b (Feb 2026) — Recovery buttons. Matches the
                  layout used by AdminCagrWarmerButton / AdminEtfWarmerButton
                  so admins have one mental model for unsticking any
                  warmer: Reset Cooldown for "let me fire again now",
                  Force Clear for "wipe the lock but keep cooldown". */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={resetCooldown}
                  data-testid="warm-non-div-reset-cooldown-btn"
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-muted/40 hover:bg-muted/60 text-foreground/80 border border-border/60"
                  title="Clear last_ran_at AND stuck in_progress flag. Use this when a previous fire left the job state inconsistent."
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset Cooldown
                </button>
                <button
                  onClick={forceClear}
                  data-testid="warm-non-div-force-clear-btn"
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border border-rose-500/40"
                  title="Nuclear: wipe in_progress lock without resetting the cooldown timer. Use when a worker crashed mid-run."
                >
                  <Unlock className="w-3.5 h-3.5" />
                  Force Clear Lock
                </button>
              </div>

              <p className="text-[10px] text-muted-foreground italic">
                Cron runs nightly automatically. Each run processes 800 of the missing tickers (rate-limited at 1.5s/ticker so Massive doesn&apos;t throttle). Full 4,393-ticker universe completes in ~6 nightly runs.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CoverageRow({ label, current, total, pct, tone }) {
  const barColor =
    tone === 'amber' ? 'bg-amber-500'
    : tone === 'sky' ? 'bg-sky-500'
    : 'bg-emerald-500';
  return (
    <div className="space-y-1" data-testid={`coverage-row-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono">
          <span className="font-bold text-foreground">{current.toLocaleString()}</span>
          <span className="text-muted-foreground"> / {total.toLocaleString()}</span>
          <span className="ml-2 font-bold">{pct}%</span>
        </span>
      </div>
      <div className="h-1.5 bg-muted/40 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
